/**
 * Open a Gmail compose window that Gmail saves into Drafts.
 * Call openMailDraftWindow() in the same click as the button — before any await —
 * so the browser does not block the popup.
 */

const GMAIL_COMPOSE_SAFE_LENGTH = 7000;

export function buildGmailComposeHref(to: string, subject: string, body?: string) {
  const address = encodeURIComponent(String(to || '').trim());
  const title = encodeURIComponent(String(subject || '').trim());
  const parts = [
    'https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=1',
    `&to=${address}`,
    `&su=${title}`,
  ];
  if (body) parts.push(`&body=${encodeURIComponent(body)}`);
  return parts.join('');
}

export function openMailDraftWindow(): Window | null {
  const win = window.open('about:blank', 'mentology-gmail-draft');
  if (!win) return null;
  try {
    win.document.open();
    win.document.write(
      '<!doctype html><title>Opening Gmail draft</title>'
      + '<p style="font-family:Georgia,serif;padding:2rem;color:#333">Opening Gmail Drafts…</p>',
    );
    win.document.close();
  } catch {
    /* named window may already be on mail.google.com */
  }
  return win;
}

export async function fillGmailDraft(
  win: Window | null,
  {
    to,
    subject,
    body,
  }: {
    to: string;
    subject: string;
    body: string;
  },
): Promise<{ opened: boolean; bodyCopied: boolean }> {
  const address = String(to || '').trim();
  const title = String(subject || '').trim();
  const message = String(body || '');
  if (!address || !title) {
    try { win?.close(); } catch { /* ignore */ }
    return { opened: false, bodyCopied: false };
  }

  let href = buildGmailComposeHref(address, title, message);
  let bodyCopied = false;
  if (href.length > GMAIL_COMPOSE_SAFE_LENGTH) {
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      /* To and Subject still go into the draft */
    }
    href = buildGmailComposeHref(address, title);
    bodyCopied = true;
  }

  if (win && !win.closed) {
    win.location.replace(href);
    win.focus();
    return { opened: true, bodyCopied };
  }

  const fallback = window.open(href, 'mentology-gmail-draft');
  return { opened: Boolean(fallback), bodyCopied };
}

export function describeGmailDraftResult(result: { opened: boolean; bodyCopied: boolean }) {
  if (!result.opened) {
    return 'Allow pop-ups for this site so Gmail can open a draft. Then click SEND again.';
  }
  if (result.bodyCopied) {
    return 'A Gmail draft is open with To and Subject filled. The message was copied — paste it into the draft, review, then Send. You can also find it under Drafts.';
  }
  return 'A Gmail draft is open. Review To, Subject, and the message, then click Send. You can also find it under Drafts.';
}
